import { useEffect, useState } from 'react';
import { getContractPdfBytes, pdfObjectUrl } from '@/modules/templates/services/docx-convert.service';
import type { FixedDocxDocument } from './document-payload';

interface Result {
  url: string | null;
  error: string | null;
  isLoading: boolean;
}

/**
 * Resolve a viewable PDF URL for a locked contract, in order of authority:
 *
 *   1. `SigDocumentUrl` — the finalized signed PDF, signatures baked in.
 *   2. `Document.url` — legacy. Contracts no longer store a PDF, because one
 *      goes stale the moment the .docx is edited.
 *   3. Derived from the stored .docx.
 *
 * Deriving is faithful here: the contract is locked, so its .docx can no longer
 * change and re-converting reproduces what the signatories received. The blob
 * URL is revoked on unmount.
 */
export function useContractPdfUrl({
  document,
  sigDocumentUrl,
  slug,
}: {
  document: FixedDocxDocument | null;
  sigDocumentUrl: string | null | undefined;
  slug: string;
}): Result {
  const storedUrl = sigDocumentUrl ?? document?.url ?? null;
  const docxUrl = document?.docxUrl;
  const docxFileId = document?.docxFileId;

  const [derivedUrl, setDerivedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const shouldDerive = !storedUrl && !!docxUrl;

  useEffect(() => {
    if (!shouldDerive || !docxUrl) return;
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const bytes = await getContractPdfBytes({
          docxUrl,
          docxFileId,
          fileName: `${slug || 'document'}.docx`,
        });
        if (cancelled) return;
        objectUrl = pdfObjectUrl(bytes);
        setDerivedUrl(objectUrl);
      } catch (err) {
        console.error('Failed to derive the contract PDF:', err);
        if (!cancelled) setError('Could not prepare this document for preview.');
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [shouldDerive, docxUrl, docxFileId, slug]);

  return {
    url: storedUrl ?? derivedUrl,
    error,
    isLoading: shouldDerive && !derivedUrl && !error,
  };
}
